/**
 * utils/history.js
 * Builders for riwayat (HistoryEntry) records that are NOT produced by the engine.
 * The transformation engine builds its own TRANSFORMASI entries (see transformations.js);
 * penyesuaian entries are built inline in App.js. This module covers item creation and
 * (Phase T1f) a transaction's own stock-moving effect.
 *
 * HistoryEntry shape (see CLAUDE.md):
 *   { id, itemCode, date, kind, bucket, delta, balanceAfter, ref, note, contactId? }
 *
 * Also exports `sortHistoryDesc` (Riwayat Stok ordering fix) — the shared newest-first
 * comparator every Riwayat Stok view should sort with, see its own comment below.
 */
import { generateId } from "./idGenerators";

/**
 * Creation entries for a freshly-made item (kind "PEMBUATAN").
 * One entry per bucket whose starting stock is non-zero (positive or negative), so
 * the opening balance of each bucket is visible in riwayat. If both buckets are
 * exactly 0, a single zero entry is emitted so the item's history is never empty.
 *
 * @param {object} item - must have internalCode, stokPusat, stokCabang
 * @param {string} [date] - ISO timestamp; defaults to now
 * @param {string|null} [contactId] - contact this opening stock came from (e.g. a supplier);
 *   stamped onto every entry, including the zero entry — uniformity is simpler than special-
 *   casing. null (the default) for every existing caller that doesn't pass one.
 * @returns {Array<object>} 1–2 HistoryEntry objects
 */
export function creationEntries(item, date = new Date().toISOString(), contactId = null) {
  const pusat = Number(item.stokPusat) || 0;
  const cabang = Number(item.stokCabang) || 0;

  const make = (bucket, qty, note) => ({
    id: generateId(),
    itemCode: item.internalCode,
    date,
    kind: "PEMBUATAN",
    bucket,
    delta: qty,
    balanceAfter: qty,
    ref: null,
    note,
    contactId,
  });

  const entries = [];
  if (pusat !== 0) entries.push(make("PUSAT", pusat, "Stok awal"));
  if (cabang !== 0) entries.push(make("CABANG", cabang, "Stok awal"));
  if (entries.length === 0) entries.push(make("PUSAT", 0, "Barang dibuat"));
  return entries;
}

/**
 * Riwayat entries for a transaction's own stock effect (Phase T1f — the PRD's §4.2 stock
 * deltas were never wired into item history, so Riwayat Stok showed nothing for
 * PENJUALAN/PEMBELIAN/TRANSFER). One entry per LINE (not merged/aggregated, unlike the
 * engine's own TRANSFORMASI entries) — PENJUALAN/PEMBELIAN get one entry per line; TRANSFER
 * gets TWO (source bucket minus, destination bucket plus), mirroring `applyTxnStockDelta`'s
 * own per-type bucket rules exactly so this builder can never disagree with the real stock
 * effect it's describing.
 *
 * `sign` mirrors `applyTxnStockDelta`'s own contract: +1 records this movement happening now
 * (create, or an edit's new-state half); -1 records a movement being REVERSED (an edit's
 * old-state half, or a delete) — same builder, no parallel "undo" formula to keep in sync.
 * `itemsAfter` must be the items array with THIS sign's delta already applied, so
 * `balanceAfter` reflects the item's real resulting bucket value at this point — the same
 * contract `App.applyAdjustment`'s PENYESUAIAN entries already follow.
 * `suffix` (optional) appends a parenthetical to the note — used to mark an edit's/delete's
 * compensating entries as such (" (dikoreksi)" / " (dibatalkan)") so they read distinctly
 * from a plain creation entry in an item's riwayat, never silently.
 * `ref` is the transaction's own id, the same "trace an entry back to its origin record"
 * convention `TRANSFORMASI` entries already use (`ref === transformation.id`).
 *
 * `editId` (optional, section E2) stamps every entry with the id of the ONE save that wrote it
 * (the same id goes on that save's editLog entry), so a save's entries are linked to each other
 * and to its edit-log entry. Absent on entries written before E2 — never backfilled.
 * Since E2 this builds a transaction's CREATION entries and a DELETE's "(dibatalkan)" reversal
 * only; an EDIT writes `transactionEditEntries` (below) instead.
 *
 * @returns {Array<object>} HistoryEntry objects, one (PENJUALAN/PEMBELIAN) or two (TRANSFER)
 *   per line in `txn.lines`.
 */
export function transactionEntries(txn, itemsAfter, sign = 1, suffix = "", editId = null) {
  const bucketField = (bucket) => (bucket === "CABANG" ? "stokCabang" : "stokPusat");
  const byCode = new Map((itemsAfter || []).map((it) => [it.internalCode, it]));
  const balanceAfter = (code, bucket) => {
    const it = byCode.get(code);
    return it ? Number(it[bucketField(bucket)]) || 0 : 0;
  };

  const make = (line, bucket, baseDelta, note) => ({
    id: generateId(),
    itemCode: line.internalCode,
    date: `${txn.date}T${txn.time || "00:00"}:00`,
    kind: txn.type,
    bucket,
    delta: sign * baseDelta,
    balanceAfter: balanceAfter(line.internalCode, bucket),
    ref: txn.id,
    note,
    contactId: null,
    ...(editId ? { editId } : {}),
  });

  const lines = txn.lines || [];
  const entries = [];
  if (txn.type === "PENJUALAN") {
    const note = `${txn.invoiceNumber || "—"}${suffix}`;
    for (const line of lines) entries.push(make(line, txn.location, -(Number(line.qty) || 0), note));
  } else if (txn.type === "PEMBELIAN") {
    const note = `${txn.supplierInvoiceNo || "—"}${suffix}`;
    for (const line of lines) entries.push(make(line, txn.location, Number(line.qty) || 0, note));
  } else if (txn.type === "TRANSFER") {
    const note = `${txn.invoiceNumber || "—"}${suffix}`;
    const destBucket = txn.location === "PUSAT" ? "CABANG" : "PUSAT";
    for (const line of lines) {
      entries.push(make(line, txn.location, -(Number(line.qty) || 0), note));
      entries.push(make(line, destBucket, Number(line.qty) || 0, note));
    }
  }
  return entries;
}

/**
 * The stock-history entries for an EDIT of a transaction (section E2, decision 2026-10-08 "1a").
 * ONE entry per item per bucket whose stock actually moved, with the NET change — never the old
 * "+5 (dikoreksi) / −3" undo-and-redo pair, which left the reader to work out that stock went up
 * by 2. An edit that doesn't move stock at all (due date, payment, address, price) writes NO
 * entry: the change lives in the transaction's own edit log, which is where it belongs.
 *   note: "<invoice> diubah (5 → 3)" — the item's total qty on this transaction before → after
 *         (0 when this edit added or removed it).
 *   date: the transaction's (new) business date, the same convention as every transaction entry.
 * Append-only like everything else: a past entry is never touched. `itemsAfter` = the items with
 * the edit fully applied (so `balanceAfter` is real). `editId` links this save's entries to its
 * edit-log entry.
 */
export function transactionEditEntries(before, after, itemsAfter, editId = null) {
  const bucketField = (bucket) => (bucket === "CABANG" ? "stokCabang" : "stokPusat");
  const byCode = new Map((itemsAfter || []).map((it) => [it.internalCode, it]));
  const effect = (txn) => {
    const m = new Map(); // "code|bucket" -> stock delta
    const add = (code, bucket, d) => { const k = `${code}|${bucket}`; m.set(k, (m.get(k) || 0) + d); };
    for (const line of txn.lines || []) {
      const q = Number(line.qty) || 0;
      if (txn.type === "PENJUALAN") add(line.internalCode, txn.location, -q);
      else if (txn.type === "PEMBELIAN") add(line.internalCode, txn.location, q);
      else if (txn.type === "TRANSFER") {
        add(line.internalCode, txn.location, -q);
        add(line.internalCode, txn.location === "PUSAT" ? "CABANG" : "PUSAT", q);
      }
    }
    return m;
  };
  const qtyOf = (txn, code) => (txn.lines || []).reduce((sum, l) => (l.internalCode === code ? sum + (Number(l.qty) || 0) : sum), 0);
  const was = effect(before);
  const now = effect(after);
  // Order: the edited transaction's own line order, then items only the old version had.
  const keys = [...new Set([...now.keys(), ...was.keys()])];
  const invoice = after.type === "PEMBELIAN" ? (after.supplierInvoiceNo || "—") : (after.invoiceNumber || "—");
  const entries = [];
  for (const k of keys) {
    const delta = (now.get(k) || 0) - (was.get(k) || 0);
    if (delta === 0) continue;
    const [code, bucket] = k.split("|");
    const it = byCode.get(code);
    entries.push({
      id: generateId(),
      itemCode: code,
      date: `${after.date}T${after.time || "00:00"}:00`,
      kind: after.type,
      bucket,
      delta,
      balanceAfter: it ? Number(it[bucketField(bucket)]) || 0 : 0,
      ref: after.id,
      note: `${invoice} diubah (${qtyOf(before, code)} → ${qtyOf(after, code)})`,
      contactId: null,
      ...(editId ? { editId } : {}),
    });
  }
  return entries;
}

/**
 * Sort HistoryEntry objects newest-first for display (Riwayat Stok ordering fix).
 *
 * Primary key is `date` — but `date` is a BUSINESS-record timestamp (see `transactionEntries`
 * above), not an audit/insertion timestamp, and it CAN legitimately tie: editing a transaction
 * to swap a line's item appends a same-item "(dikoreksi)" reversal entry stamped with the
 * SAME `date` as the original entry it corrects (neither call threads through a real "now").
 * A plain `sort by date descending` leaves tied entries in their pre-sort (stable-sort)
 * order, which is real chronological INSERTION order — so the OLDER entry (inserted first)
 * stays ahead of its own later correction. Backwards.
 *
 * Secondary key: each entry's own `id`, which already embeds a real insertion timestamp
 * (`generateId()` is `${Date.now()}-${random}` — see idGenerators.js). No new field, no
 * migration, and it works retroactively on every entry ever created. Deliberately NOT a fix
 * to `date` itself — a transaction's business date is legitimately backdatable (editing it
 * across a month boundary never re-mints its invoice number), so a correction to a backdated
 * transaction should stay grouped near that period, not jump to "today."
 *
 * Compared to the MINUTE, not the second (same-minute ordering fix, 2026-10-09). A transaction's
 * date only has minute precision ("14:11" → 14:11:00), while a Penyesuaian / item creation
 * records the exact second (14:11:40). A purchase saved at 14:11:50 used to sort BELOW a
 * Penyesuaian saved at 14:11:40, contradicting the Saldo column. Within one minute nothing is
 * more trustworthy than the order things were actually saved, so a same-minute tie falls through
 * to the insertion sequence. Entries in different minutes still sort by their own date, so a
 * backdated transaction keeps its place in that period.
 */
function insertionSeq(id) {
  const n = Number(String(id).split("-")[0]);
  return Number.isFinite(n) ? n : 0;
}

function minuteOf(date) {
  const ms = new Date(date).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 60000) : 0;
}

export function sortHistoryDesc(entries) {
  return [...entries].sort((a, b) => {
    const byMinute = minuteOf(b.date) - minuteOf(a.date);
    return byMinute !== 0 ? byMinute : insertionSeq(b.id) - insertionSeq(a.id);
  });
}
